import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Upload, FileCheck, MapPin, UtensilsCrossed, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "medical", "dental", "mechanic", "locksmith", "hotel",
  "spa", "restaurant", "beauty", "fitness", "veterinary", 
  "hairdresser", "private_clinic", "pizzeria", "bar", "parking",
  "lawyer", "accountant", "notary", "architect", "engineer",
  "psychologist", "physiotherapist", "optician", "pharmacy",
  "tailor", "plumber", "electrician", "painter", "carpenter",
  "driving_school", "photographer", "florist", "bakery",
  "butcher", "ice_cream", "sushi", "kebab", "takeaway_pizza",
  "coworking", "laundry", "pet_grooming", "tattoo", "barber",
  "other"
];

// Categories that can charge deposit (hospitality + clinics + professionals)
const DEPOSIT_CATEGORIES = ["restaurant", "hotel", "spa", "private_clinic", "pizzeria", "bar", "parking", "takeaway_pizza", "sushi", "kebab", "bakery", "coworking"];

// Categories with tables
const TABLE_CATEGORIES = ["restaurant", "hotel", "pizzeria", "bar", "sushi", "coworking", "beach_club"];

// Categories with menu
const MENU_CATEGORIES = ["restaurant", "pizzeria", "bar", "sushi", "kebab", "takeaway_pizza", "bakery", "ice_cream", "butcher", "beach_club"];

export default function BusinessRegister() {
  const { setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    category: "",
    description: "",
    address: "",
    phone: "",
    deposit_amount: 0,
    total_tables: 0,
    latitude: null,
    longitude: null,
    satispay_phone: "",
  });

  const canHaveDeposit = DEPOSIT_CATEGORIES.includes(form.category);
  const canHaveTables = TABLE_CATEGORIES.includes(form.category);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          toast.success("Posizione rilevata!");
        },
        (error) => {
          toast.error("Impossibile ottenere la posizione");
        }
      );
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };
      // Reset deposit if category doesn't support it
      if (field === "category" && !DEPOSIT_CATEGORIES.includes(value)) {
        newForm.deposit_amount = 0;
      }
      if (field === "category" && !TABLE_CATEGORIES.includes(value)) {
        newForm.total_tables = 0;
      }
      return newForm;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      toast.error("Name and category are required");
      return;
    }
    setLoading(true);
    try {
      const bizRes = await axios.post(`${API}/businesses`, form, { withCredentials: true });
      const bizId = bizRes.data.business_id;
      // Upload documents if any
      for (const doc of uploadedDocs) {
        const formData = new FormData();
        formData.append("file", doc);
        await axios.post(`${API}/businesses/${bizId}/documents`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userRes.data);
      toast.success(t("biz.submit") + "!");
      navigate("/business", { replace: true });
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadedDocs((prev) => [...prev, ...files]);
    toast.success(`${files.length} ${t("doc.uploaded")}`);
  };

  const removeDoc = (index) => {
    setUploadedDocs((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="pt-32 pb-20 px-6 lg:px-16">
        <div className="max-w-2xl mx-auto">
          <h1
            className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#2A1B14] mb-4"
            data-testid="register-title"
          >
            {t("biz.register.title")}
          </h1>
          <p className="text-sm text-slate-500 mb-4 leading-relaxed">
            Complete the form below to register your business on BookEasy.
          </p>

          {/* Promo 30 giorni */}
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-2xl p-5 mb-8" data-testid="trial-promo-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">30</span>
              </div>
              <div>
                <h3 className="font-black text-emerald-800 text-base">30 giorni GRATIS!</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  I primi 30 giorni dall'iscrizione sono completamente gratuiti: <strong>nessuna commissione</strong> e <strong>nessuna cauzione</strong> per i tuoi clienti.
                </p>
                <p className="text-sm text-emerald-600 mt-2">
                  Dopo il periodo di prova, verra applicata una commissione di <strong>1€ per prenotazione</strong>, scalata direttamente al cliente al momento del pagamento definitivo. Tu non paghi nulla.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                {t("biz.name")} *
              </label>
              <Input
                data-testid="biz-name-input"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="rounded-xl border-orange-200 h-12 focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                required
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                {t("biz.category")} *
              </label>
              <Select value={form.category} onValueChange={(v) => updateField("category", v)}>
                <SelectTrigger
                  className="rounded-xl border-orange-200 h-12 bg-white"
                  data-testid="biz-category-select"
                >
                  <SelectValue placeholder={t("biz.category")} />
                </SelectTrigger>
                <SelectContent className="rounded-xl bg-white">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="rounded-lg">
                      {t(`cat.${cat}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                {t("biz.description")}
              </label>
              <Textarea
                data-testid="biz-description-input"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                className="rounded-xl border-orange-200 min-h-[100px] focus:ring-2 focus:ring-orange-300"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                  {t("biz.address")}
                </label>
                <Input
                  data-testid="biz-address-input"
                  value={form.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  className="rounded-xl border-orange-200 h-12"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                  {t("biz.phone")}
                </label>
                <Input
                  data-testid="biz-phone-input"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className="rounded-xl border-orange-200 h-12"
                />
              </div>
            </div>

            {/* Satispay Phone */}
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-5 w-5 text-red-500" />
                <span className="text-xs uppercase tracking-widest text-red-800 font-bold">
                  Satispay (Opzionale)
                </span>
              </div>
              <p className="text-xs text-red-700 mb-4">
                Inserisci il tuo numero Satispay per ricevere pagamenti direttamente dall'app dei clienti
              </p>
              <Input
                data-testid="biz-satispay-phone-input"
                value={form.satispay_phone}
                onChange={(e) => updateField("satispay_phone", e.target.value)}
                placeholder="+39 333 1234567"
                className="rounded-xl border-red-300 h-12 bg-white"
              />
            </div>

            {/* Geolocation */}
            <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-sky-600" />
                  <span className="text-xs uppercase tracking-widest text-sky-800 font-bold">
                    Posizione (per ricerca geolocalizzata)
                  </span>
                </div>
                <Button
                  type="button"
                  onClick={getLocation}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-sky-300 text-sky-700 hover:bg-sky-100"
                  data-testid="get-location-btn"
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  {t("geo.enable")}
                </Button>
              </div>
              {form.latitude && form.longitude ? (
                <p className="text-sm text-sky-700">
                  Posizione rilevata: {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
                </p>
              ) : (
                <p className="text-xs text-sky-600">
                  Attiva la posizione per permettere ai clienti di trovarti in base alla vicinanza
                </p>
              )}
            </div>

            {/* Tables Section - For restaurants, bars, pizzerias */}
            {canHaveTables && (
              <div className="rounded-2xl border border-purple-200 bg-purple-50 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <UtensilsCrossed className="h-5 w-5 text-purple-600" />
                  <span className="text-xs uppercase tracking-widest text-purple-800 font-bold">
                    Numero Tavoli (opzionale)
                  </span>
                </div>
                <p className="text-xs text-purple-700 mb-4">
                  Imposta il numero di tavoli della tua attività. Potrai gestirli dalla dashboard.
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    value={form.total_tables}
                    onChange={(e) => updateField("total_tables", parseInt(e.target.value) || 0)}
                    min={0}
                    max={500}
                    className="rounded-xl border-purple-300 h-12 w-[150px] bg-white"
                    data-testid="tables-input"
                  />
                  <span className="text-sm text-purple-800">tavoli</span>
                </div>
              </div>
            )}

            {/* Deposit Section - Only for restaurants, hotels, spas, private clinics */}
            {canHaveDeposit && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
                <label className="text-xs uppercase tracking-widest text-amber-800 mb-2 block font-bold">
                  {t("biz.deposit")} (Opzionale)
                </label>
                <p className="text-xs text-amber-700 mb-4">
                  Puoi richiedere una cauzione ai clienti al momento della prenotazione. 
                  Decidi tu l'importo (0€ = nessuna cauzione, oppure 1€, 1.50€, 2€, ecc.)
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    data-testid="biz-deposit-input"
                    type="number"
                    step="0.50"
                    min="0"
                    max="50"
                    value={form.deposit_amount}
                    onChange={(e) => updateField("deposit_amount", parseFloat(e.target.value) || 0)}
                    className="rounded-xl border-amber-300 h-12 w-[150px] bg-white"
                  />
                  <span className="text-sm text-amber-800 font-medium">EUR</span>
                </div>
                <div className="mt-4 p-3 bg-white border border-amber-200 rounded-xl">
                  <p className="text-xs text-slate-500">
                    <strong>Il cliente pagherà:</strong> {form.deposit_amount > 0 ? `${form.deposit_amount.toFixed(2)}€ (tua cauzione)` : "0€ (nessuna cauzione)"} + <strong>1.00€</strong> (commissione piattaforma BookEasy)
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>Totale per il cliente:</strong> {(form.deposit_amount + 1).toFixed(2)}€
                  </p>
                </div>
              </div>
            )}

            {/* Info for categories without deposit */}
            {form.category && !canHaveDeposit && (
              <div className="border border-blue-200 bg-blue-50 p-6">
                <p className="text-xs text-blue-800">
                  <strong>Nota:</strong> Per la categoria "{t(`cat.${form.category}`)}" non è prevista cauzione. 
                  I clienti pagheranno solo <strong>1.00€</strong> di commissione piattaforma per confermare la prenotazione.
                </p>
              </div>
            )}

            {/* Document Upload Section */}
            <div className="border-t border-orange-100 pt-6">
              <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">
                {t("doc.upload")}
              </label>
              <p className="text-xs text-slate-400 mb-4">{t("doc.upload.desc")}</p>
              <div className="border-2 border-dashed border-orange-200 p-6 bg-white hover:border-orange-400 transition-colors duration-200 rounded-xl">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="doc-upload"
                  data-testid="doc-upload-input"
                />
                <label htmlFor="doc-upload" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-orange-400 mb-3" />
                  <span className="text-sm text-slate-500">{t("doc.select")}</span>
                </label>
              </div>
              {uploadedDocs.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedDocs.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white border border-orange-100 rounded-xl">
                      <div className="flex items-center gap-2">
                        <FileCheck className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm text-slate-800">{doc.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDoc(i)}
                        className="text-xs text-red-500 hover:underline"
                        data-testid={`remove-doc-${i}`}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                data-testid="register-business-button"
                disabled={loading}
                className="rounded-full px-8 py-6 text-base bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white hover:shadow-xl hover:shadow-[#E2725B]/25 border-0 transition-all duration-200 disabled:opacity-50 font-bold hover:scale-105 active:scale-95"
              >
                {loading ? t("common.loading") : t("biz.submit")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
